import { useTranslation } from "@i18n";
import { businessTimeBetween, type SlaState } from "@Helpers";
import type { SlaClock } from "@ServerTypes/sla/sla.schema";
import { useNow, formatDuration, formatInstant } from "./useSlaCountdown";

type Variant = "response" | "resolution";

const COUNTDOWN_TICK_MS = 30_000;

const STATE_CLASS: Record<SlaState, { accent: string; bg: string }> = {
  onTrack: { accent: "bg-sla_on_track", bg: "bg-sla_on_track_bg" },
  approaching: { accent: "bg-sla_approaching", bg: "bg-sla_approaching_bg" },
  met: { accent: "bg-sla_complete", bg: "bg-sla_complete_bg" },
  breachedOpen: { accent: "bg-sla_pending", bg: "bg-sla_pending_bg" },
  breachedResolved: { accent: "bg-sla_breached", bg: "bg-sla_breached_bg" },
  paused: { accent: "bg-sla_breached", bg: "bg-sla_breached_bg" },
};

export function SlaCard({
  variant,
  clock,
  useWallClock = false,
}: {
  variant: Variant;
  clock: SlaClock;
  useWallClock?: boolean;
}) {
  const { t } = useTranslation();
  const ticking = !clock.stopped && clock.actualAt == null;
  const now = useNow(ticking, COUNTDOWN_TICK_MS);
  const bizToDeadline = ticking ? msToDeadline(clock, now, useWallClock) : 0;
  const state = liveState(clock, now, bizToDeadline);
  const colorState = state === "paused" ? pausedColorState(clock) : state;
  const cls = STATE_CLASS[colorState];

  const badgeLabel =
    state === "onTrack"
      ? t("On Track")
      : state === "approaching"
        ? t("Approaching")
        : state === "met"
          ? t("Complete")
          : state === "breachedOpen" || state === "breachedResolved"
            ? t("Breached")
            : t("Paused");

  let headline: string;
  if (state === "met") headline = variant === "response" ? t("Responded in time") : t("Resolved in time");
  else if (state === "breachedResolved") headline = t("SLA breached");
  else if (state === "paused") {
    const remaining = pausedRemainingMs(clock);
    headline = remaining > 0 ? t("{{value}} remaining", { value: formatDuration(remaining) }) : t("SLA Paused");
  } else if (state === "breachedOpen")
    headline =
      clock.deadlineAt != null ? t("{{value}} exceeded", { value: formatDuration(bizToDeadline) }) : t("SLA breached");
  else headline = clock.deadlineAt != null ? t("{{value}} remaining", { value: formatDuration(bizToDeadline) }) : "-";

  const deadlineValue =
    state === "paused" ? t("Paused") : clock.deadlineAt != null ? formatInstant(clock.deadlineAt) : "-";
  const actualValue = clock.actualAt != null ? formatInstant(clock.actualAt) : t("Awaited");

  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border border-stroke_primary_light bg-white p-4 ${
        state === "paused" ? "opacity-50" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-primary_text">
          {variant === "response" ? t("First Response") : t("Resolution")}
        </span>
        <span
          className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold text-primary_text ${cls.bg}`}
        >
          <span className={`h-2 w-2 rounded-full ${cls.accent}`} />
          {badgeLabel}
        </span>
      </div>

      <div className="flex items-center gap-2.5">
        <span className={`h-4 w-4 shrink-0 rounded-full ${cls.accent}`} aria-hidden />
        <span className="text-base font-semibold text-primary_text">{headline}</span>
      </div>

      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface_neutral">
        <div
          className={`h-full rounded-full ${cls.accent}`}
          style={{ width: `${progressPct(clock, state, bizToDeadline)}%` }}
        />
      </div>

      <div className="flex gap-3">
        <InfoBox label={t("SLA Deadline")} value={deadlineValue} />
        <InfoBox
          label={variant === "response" ? t("Actual Response") : t("Actual Resolution")}
          value={actualValue}
          valueClass={actualColorClass(variant, state, clock.actualAt != null)}
        />
      </div>
    </div>
  );
}

function InfoBox({
  label,
  value,
  valueClass = "text-primary_text",
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex-1 rounded-md border border-stroke_primary_light p-2">
      <div className="text-xs text-text_light">{label}</div>
      <div className={`text-sm font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
}

function actualColorClass(variant: Variant, state: SlaState, hasActual: boolean): string {
  if (hasActual) return state === "breachedResolved" ? "text-text_light" : "text-primary_text";
  if (state === "breachedOpen") return "text-negative_communication";
  if (variant === "response" && state === "approaching") return "text-warning";
  return "text-primary_text";
}

function pausedColorState(clock: SlaClock): SlaState {
  return clock.targetMs - clock.accumulatedRunningMs <= 0.25 * clock.targetMs ? "approaching" : "onTrack";
}

// Remaining time frozen at the moment of pause. While paused runningSince is
// null, so consumed === accumulatedRunningMs and remaining === targetMs - consumed.
function pausedRemainingMs(clock: SlaClock): number {
  return clock.targetMs - clock.accumulatedRunningMs;
}

function msToDeadline(clock: SlaClock, now: number, useWallClock: boolean): number {
  if (clock.deadlineAt == null) return 0;
  if (useWallClock) return Math.abs(clock.deadlineAt - now);
  return now <= clock.deadlineAt
    ? businessTimeBetween(now, clock.deadlineAt)
    : businessTimeBetween(clock.deadlineAt, now);
}

function liveState(clock: SlaClock, now: number, bizToDeadline: number): SlaState {
  if (clock.stopped || clock.actualAt != null) return clock.state;
  if (clock.paused) return clock.state === "breachedOpen" ? "breachedOpen" : "paused"; // RED WINS over paused
  if (clock.deadlineAt != null && now > clock.deadlineAt) return "breachedOpen";
  if (clock.deadlineAt != null) {
    return bizToDeadline <= 0.25 * clock.targetMs ? "approaching" : "onTrack";
  }
  return clock.state;
}

function progressPct(clock: SlaClock, state: SlaState, bizToDeadline: number): number {
  if (state === "met" || state === "breachedResolved" || state === "breachedOpen") return 100;
  if (state === "paused" || clock.deadlineAt == null) {
    return clamp((clock.accumulatedRunningMs / clock.targetMs) * 100);
  }
  return clamp((1 - bizToDeadline / clock.targetMs) * 100);
}

function clamp(n: number): number {
  return Math.max(0, Math.min(100, n));
}
